import type {
  ServiceRequestStatus,
  ServiceRequestType,
  ServiceRequestUserDto,
} from '@smartsash/api-client';
import type { BadgeColor } from '@smartsash/ui';

import type { TranslationKey } from '@/types/i18n';

/**
 * Presentation of a service request shared by the list and the details page (HS-1548): type
 * and status labels, the status colour, the person-name fallback chain and the date format.
 */

export const TYPE_LABEL_KEYS = {
  AccessExtension: 'serviceRequests.types.AccessExtension',
  AdditionalPermissions: 'serviceRequests.types.AdditionalPermissions',
  Repair: 'serviceRequests.types.Repair',
  Support: 'serviceRequests.types.Support',
  Message: 'serviceRequests.types.Message',
} as const satisfies Record<ServiceRequestType, TranslationKey>;

export const STATUS_LABEL_KEYS = {
  New: 'serviceRequests.statuses.New',
  Completed: 'serviceRequests.statuses.Completed',
  Rejected: 'serviceRequests.statuses.Rejected',
  Withdrawn: 'serviceRequests.statuses.Withdrawn',
} as const satisfies Record<ServiceRequestStatus, TranslationKey>;

/** `New` is the one that asks for action; the closed states read as outcomes. */
export const STATUS_COLORS = {
  New: 'blue',
  Completed: 'success',
  Rejected: 'error',
  Withdrawn: 'gray',
} as const satisfies Record<ServiceRequestStatus, BadgeColor>;

export const SERVICE_REQUEST_TYPES = Object.keys(TYPE_LABEL_KEYS) as ServiceRequestType[];
export const SERVICE_REQUEST_STATUSES = Object.keys(STATUS_LABEL_KEYS) as ServiceRequestStatus[];

/** The label key of a type the client knows, or `undefined` for one added later by the backend. */
export function typeLabelKey(type: string): TranslationKey | undefined {
  return Object.hasOwn(TYPE_LABEL_KEYS, type)
    ? TYPE_LABEL_KEYS[type as ServiceRequestType]
    : undefined;
}

/** The label key of a status the client knows, or `undefined`. */
export function statusLabelKey(status: string): TranslationKey | undefined {
  return Object.hasOwn(STATUS_LABEL_KEYS, status)
    ? STATUS_LABEL_KEYS[status as ServiceRequestStatus]
    : undefined;
}

/** A status the client does not know renders neutral. */
export function statusColor(status: string): BadgeColor {
  return Object.hasOwn(STATUS_COLORS, status)
    ? STATUS_COLORS[status as ServiceRequestStatus]
    : 'gray';
}

/**
 * A person's name as the page prints it (E14a): `fullName` is `""` when the user row is gone,
 * so fall back to the email, and then to `undefined` — the caller shows "Unknown user".
 */
export function personName(person: ServiceRequestUserDto | null | undefined): string | undefined {
  if (person == null) return undefined;
  const fullName = person.fullName.trim();
  if (fullName !== '') return fullName;
  const email = person.email?.trim();
  return email != null && email !== '' ? email : undefined;
}

/** An API instant in the viewer's time zone and language — "26 Sept 2026, 17:18". */
export function formatRequestDateTime(instant: string, locale: string): string {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return instant;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/** An API instant as a calendar day only — for access-end dates. */
export function formatRequestDate(instant: string, locale: string): string {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return instant;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
}
